import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, Avatar } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useQuery } from 'react-query'
import getGamesByContext from '../../../../Apis/event/getGamesByContext'
import QueryKeys from '../../../../utils/constants/queryKeys'
import buildBracketRounds from '../../../../utils/helpers/buildBracketRounds'
import { useTeamCalendarContext } from '../../../../context/useTeamCalendarContext'
import CalendarEventClickModal from '../../CalendarEventClickModal/CalendarEventClickModal'
import LoaderDots from '../../../../components/loaders/LoaderDots/LoaderDots'
import ContainerPadding from '../../../../components/containerPadding/containerPadding'
import COLORS from '../../../../utils/constants/colors'
import calendarEventModalDimensions from '../../../../utils/constants/calendarEventModalDimensions'
import bracketStyles from './bracket.styles'

const useStyles = makeStyles(bracketStyles)

const ScoreCell = ({ gameId, value, won, className, onGameClick }) => (
  <button
    type='button'
    className={`${className} ${won ? 'is-win' : ''}`}
    onClick={(e) => gameId && onGameClick(e, gameId)}
    disabled={!gameId}
  >
    {value == null ? '-' : value}
  </button>
)

ScoreCell.propTypes = {
  gameId: PropTypes.string,
  value: PropTypes.number,
  won: PropTypes.bool,
  className: PropTypes.string,
  onGameClick: PropTypes.func,
}

const TeamRow = ({ team, scores, classes, onGameClick }) => (
  <div className={classes.teamRow}>
    <Avatar src={team.logoUrl || undefined} alt={team.name} className={classes.teamLogo}>
      {!team.logoUrl && team.name?.charAt(0)}
    </Avatar>
    <span className={classes.teamName} title={team.name}>
      {team.name || '—'}
    </span>
    <div className={classes.scoreCells}>
      {scores.map(({ gameId, value, won }, idx) => (
        <ScoreCell
          key={gameId || idx}
          gameId={gameId}
          value={value}
          won={won}
          className={classes.scoreCell}
          onGameClick={onGameClick}
        />
      ))}
    </div>
  </div>
)

TeamRow.propTypes = {
  team: PropTypes.object.isRequired,
  scores: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  onGameClick: PropTypes.func.isRequired,
}

// Per game, bold the higher score (the per-game winner), not the whole row.
const sideScores = (games, side) =>
  games.map((g) => {
    const value = side === 'A' ? g.scoreA : g.scoreB
    const other = side === 'A' ? g.scoreB : g.scoreA
    return { gameId: g.id, value, won: value != null && other != null && value > other }
  })

const SeriesCard = ({ series, classes, onGameClick }) => (
  <div className={classes.seriesCard}>
    <TeamRow team={series.teamA} scores={sideScores(series.games, 'A')} classes={classes} onGameClick={onGameClick} />
    <TeamRow team={series.teamB} scores={sideScores(series.games, 'B')} classes={classes} onGameClick={onGameClick} />
  </div>
)

SeriesCard.propTypes = {
  series: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onGameClick: PropTypes.func.isRequired,
}

const Bracket = ({ group }) => {
  const classes = useStyles()
  const { setClickedEventId, setCoordinatesModal, setDates } = useTeamCalendarContext()
  const [modalOpen, setModalOpen] = useState(false)

  const { data: games = [], isLoading } = useQuery(
    [QueryKeys.GET_GAMES_BY_CONTEXT, 'bracket', group?.id],
    () => getGamesByContext({ groupId: group?.id }),
    { enabled: Boolean(group?.id) },
  )

  const { mode, rounds } = useMemo(
    () => buildBracketRounds(games, group?.gamedays || []),
    [games, group],
  )

  const handleGameClick = (e, gameId) => {
    const { calendarEventModalWidth, calendarEventModalHeight } = calendarEventModalDimensions
    const bodyRect = document.body.getBoundingClientRect()
    const rect = e.currentTarget.getBoundingClientRect()

    // Open to the left of the clicked cell when there's room, otherwise to the right.
    let x = rect.left - bodyRect.left - calendarEventModalWidth
    if (x < 0) x = rect.right - bodyRect.left
    let y = rect.top - bodyRect.top
    if (bodyRect.height - rect.top < calendarEventModalHeight) {
      y = rect.top - bodyRect.top - calendarEventModalHeight
    }

    // The modal reads `event` from context, which is only fetched when the calendar's
    // getAllEvents query has data. Setting a wide date range guarantees that query is
    // enabled here on the Standings tab — same trick the CMS game-edit flow uses.
    const now = new Date()
    setDates({
      start: new Date(now.getFullYear() - 2, 0, 1),
      end: new Date(now.getFullYear() + 2, 11, 31),
    })
    setCoordinatesModal({ x, y })
    setClickedEventId(gameId)
    setModalOpen(true)
  }

  if (isLoading) {
    return (
      <ContainerPadding paddingValue='20%'>
        <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
      </ContainerPadding>
    )
  }

  if (rounds.every((r) => !r.series.length)) {
    return (
      <Box className={classes.empty}>
        <Typography color='textSecondary'>
          No bracket games available for this group
        </Typography>
      </Box>
    )
  }

  // 'pairs': gamedays are legs of a series — one card per pair, in a responsive grid.
  // 'rounds': gamedays are knockout rounds — one column per round.
  const content =
    mode === 'pairs' ? (
      <div className={classes.grid}>
        {(rounds[0]?.series || []).map((series) => (
          <SeriesCard
            key={series.key}
            series={series}
            classes={classes}
            onGameClick={handleGameClick}
          />
        ))}
      </div>
    ) : (
      <div className={classes.scroller}>
        <div className={classes.board}>
          {rounds.map((round) => (
            <div key={round.id} className={classes.round}>
              <div className={classes.roundHeader}>{round.name || '—'}</div>
              <div className={classes.seriesList}>
                {round.series.map((series) => (
                  <SeriesCard
                    key={series.key}
                    series={series}
                    classes={classes}
                    onGameClick={handleGameClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )

  return (
    <>
      {content}
      <CalendarEventClickModal open={modalOpen} handleClose={() => setModalOpen(false)} />
    </>
  )
}

Bracket.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    gamedays: PropTypes.array,
  }).isRequired,
}

export default Bracket
